import React, { useState, useEffect } from 'react';
import Swal from 'sweetalert2';
import 'sweetalert2/dist/sweetalert2.min.css';

const AccountDetailSection = () => {
  // Popup states
  const [showPopup, setShowPopup] = useState(false);
  const [popupImage, setPopupImage] = useState("");

  // Payment states
  const [showPayment, setShowPayment] = useState(false);
  const [selectedBank, setSelectedBank] = useState("");
  const [paymentData, setPaymentData] = useState({
    name: "",
    phone: "",
    ucc: "",
    amount: "",
    email: ""
  });

  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [apiError, setApiError] = useState("");

  // Bank details data
  const bankDetails = {
    "axis": {
      name: "AXIS BANK LTD",
      account: "917020074476573",
      ifsc: "UTIB0000007",
      upi: "asiatic.rzp.brk@validaxis"
    },
    "icici": {
      name: "ICICI BANK LTD",
      account: "000705036698",
      ifsc: "ICIC0000007",
      upi: "asiatic.brk@validicici"
    }
  };

  // API Configuration
  const API_CONFIG = {
    BASE_URL: "https://admin.asiaticstock.in",
    API_KEY: "ASSL#2025_@Stock"
  };
  
  // Get full API URL
  const getApiUrl = (endpoint) => {
    return `${API_CONFIG.BASE_URL}/api${endpoint.startsWith('/') ? endpoint : `/${endpoint}`}`;
  };

  // Get API headers
  const getApiHeaders = () => {
    return {
      'Content-Type': 'application/json',
      'Accept': 'application/json',
      'api-key': API_CONFIG.API_KEY
    };
  };

  // Load Razorpay script
  useEffect(() => {
    const script = document.createElement('script');
    script.src = 'https://checkout.razorpay.com/v1/checkout.js';
    script.async = true;
    script.onerror = () => {
      showPaymentAlert('error', 'Payment Gateway Error', 'Payment gateway failed to load. Please refresh the page.');
    };
    document.body.appendChild(script);

    return () => {
      if (document.body.contains(script)) {
        document.body.removeChild(script);
      }
    };
  }, []);

  // SweetAlert Functions
  const showPaymentAlert = (type, title, message, details = null) => {
    const config = {
      title: title,
      html: message,
      backdrop: true,
      allowOutsideClick: false,
      showConfirmButton: true,
      confirmButtonText: 'OK',
      showCloseButton: true,
      width: '450px',
      customClass: {
        popup: 'payment-status-popup',
        title: 'payment-status-title',
        htmlContainer: 'payment-status-content',
        confirmButton: 'payment-status-button'
      }
    };

    switch(type) {
      case 'success':
        config.icon = 'success';
        config.confirmButtonColor = '#28a745';
        config.iconColor = '#28a745';
        break;
        
      case 'error':
        config.icon = 'error';
        config.confirmButtonColor = '#dc3545';
        config.iconColor = '#dc3545';
        break;
        
      case 'warning':
        config.icon = 'warning';
        config.confirmButtonColor = '#ffc107';
        config.iconColor = '#ffc107';
        break;
        
      case 'info':
        config.icon = 'info';
        config.confirmButtonColor = '#17a2b8';
        config.iconColor = '#17a2b8';
        break;
        
      case 'processing':
        config.icon = 'info';
        config.showConfirmButton = false;
        config.allowOutsideClick = false;
        config.didOpen = () => {
          Swal.showLoading();
        };
        break;
    }

    return Swal.fire(config);
  };

  // Show payment success with details - UPDATED TO CLOSE PAYMENT POPUP
  const showPaymentSuccess = (paymentInfo) => {
    const { razorpay_payment_id, amount, bank, order_id, ucc } = paymentInfo;
    
    const formattedAmount = formatAmountDisplay(amount);
    const currentTime = new Date().toLocaleString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true
    });
    
    const successHTML = `
      <div class="payment-success-container">
        <div class="success-header">
          <div class="success-icon">
            <svg width="60" height="60" viewBox="0 0 60 60" fill="none">
              <circle cx="30" cy="30" r="28" fill="#28a745" fill-opacity="0.1"/>
              <path d="M20 30L27.5 37.5L40 25" stroke="#28a745" stroke-width="4" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
          </div>
          <h3 style="color: #28a745; margin: 15px 0 5px 0;">Payment Successful!</h3>
        </div>
        
        <div class="payment-details-card">
          <div class="detail-row">
            <div class="detail-label">Payment ID</div>
            <div class="detail-value">
              <code style="background: #e9ecef; padding: 4px 8px; border-radius: 4px; font-family: monospace;">${razorpay_payment_id}</code>
            </div>
          </div>
          
          <div class="detail-row">
            <div class="detail-label">Order ID</div>
            <div class="detail-value">
              <code style="background: #e9ecef; padding: 4px 8px; border-radius: 4px; font-family: monospace;">${order_id}</code>
            </div>
          </div>
          
          <div class="detail-row">
            <div class="detail-label">Amount Paid</div>
            <div class="detail-value" style="color: #28a745; font-weight: bold; font-size: 1.0em;">
              ₹${formattedAmount}
            </div>
          </div>
          
          <div class="detail-row">
            <div class="detail-label">Bank</div>
            <div class="detail-value">${bank}</div>
          </div>
          
          <div class="detail-row">
            <div class="detail-label">UCC Code</div>
            <div class="detail-value">${ucc}</div>
          </div>
          
          <div class="detail-row">
            <div class="detail-label">Date & Time</div>
            <div class="detail-value">${currentTime}</div>
          </div>
          
        </div>
        
        <div class="payment-security-info">
          <div style="display: flex; align-items: center; justify-content: center; gap: 8px; color: #6c757d;">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="#28a745">
              <path d="M12 1L3 5V11C3 16.55 6.84 21.74 12 23C17.16 21.74 21 16.55 21 11V5L12 1ZM12 11.99H19C18.47 16.11 15.72 19.78 12 20.93V12H5V6.3L12 3.19V11.99Z"/>
            </svg>
            <span>Secured by Razorpay</span>
          </div>
        </div>
        
      </div>
    `;
    
    return Swal.fire({
      title: '',
      html: successHTML,
      confirmButtonText: 'Close',
      confirmButtonColor: '#28a745',
      backdrop: true,
      allowOutsideClick: false,
      showCloseButton: true,
      width: '450px',
      customClass: {
        popup: 'payment-status-popup',
        title: 'payment-status-title',
        htmlContainer: 'payment-status-content',
        confirmButton: 'payment-status-button'
      }
    }).then((result) => {
      // Close payment popup when SweetAlert is closed
      if (result.isConfirmed || result.dismiss === Swal.DismissReason.close || result.dismiss === Swal.DismissReason.esc) {
        closePayment();
      }
    });
  };

  // Show payment error with details - UPDATED TO CLOSE PAYMENT POPUP
  const showPaymentError = (errorTitle, errorMessage, paymentId = null) => {
    const errorHTML = `
      <div class="payment-error-container">
        <div class="error-header">
          <div class="error-icon">
            <svg width="60" height="60" viewBox="0 0 60 60" fill="none">
              <circle cx="30" cy="30" r="28" fill="#dc3545" fill-opacity="0.1"/>
              <path d="M20 20L40 40M40 20L20 40" stroke="#dc3545" stroke-width="4" stroke-linecap="round"/>
            </svg>
          </div>
          <h3 style="color: #dc3545; margin: 15px 0 5px 0;">${errorTitle}</h3>
        </div>
        
        <div class="error-message">
          <p style="color: #495057; margin-bottom: 20px;">${errorMessage}</p>
        </div>
        
        ${paymentId ? `
          <div class="payment-id-info">
            <p style="font-size: 0.9em; color: #6c757d; margin: 10px 0;">
              <strong>Reference ID:</strong> ${paymentId}
            </p>
          </div>
        ` : ''}
        
        <div class="contact-support">
          <p style="font-size: 0.85em; color: #6c757d; text-align: center; margin-top: 15px;">
            For assistance, please contact our support team
          </p>
        </div>
      </div>
    `;
    
    return Swal.fire({
      title: '',
      html: errorHTML,
      confirmButtonText: 'Close',
      confirmButtonColor: '#dc3545',
      backdrop: true,
      allowOutsideClick: false,
      showCloseButton: true,
      width: '450px',
      customClass: {
        popup: 'payment-status-popup',
        title: 'payment-status-title',
        htmlContainer: 'payment-status-content',
        confirmButton: 'payment-status-button'
      }
    }).then((result) => {
      // Close payment popup when SweetAlert is closed
      if (result.isConfirmed || result.dismiss === Swal.DismissReason.close || result.dismiss === Swal.DismissReason.esc) {
        closePayment();
      }
    });
  };

  // QR Code Popup functions
  const openPopup = (img) => {
    setPopupImage(img);
    setShowPopup(true);
  };

  const closePopup = () => {
    setShowPopup(false);
    setPopupImage("");
  };

  // Payment functions - FIXED: Added forceClose parameter
  const openPayment = (bankName) => {
    setSelectedBank(bankName.toLowerCase().includes("axis") ? "axis" : "icici");
    setShowPayment(true);
    setApiError("");
  };

  const closePayment = (forceClose = false) => {
    // Don't close if submitting and not force close
    if (isSubmitting && !forceClose) return;
    
    setShowPayment(false);
    setPaymentData({ 
      name: "", 
      phone: "", 
      amount: "", 
      ucc: "",
      email: ""
    });
    setErrors({});
    setIsSubmitting(false);
    setApiError("");
  };

  // Form validation
  const validateForm = () => {
    const newErrors = {};
    
    if (!paymentData.name.trim()) {
      newErrors.name = 'Full name is required';
    }
    
    if (!paymentData.ucc.trim()) {
      newErrors.ucc = 'UCC code is required';
    }
    
    if (!paymentData.phone.trim()) {
      newErrors.phone = 'Phone number is required';
    } else if (!/^\d{10}$/.test(paymentData.phone)) {
      newErrors.phone = 'Phone number must be 10 digits';
    }
    
    if (paymentData.email && !/\S+@\S+\.\S+/.test(paymentData.email)) {
      newErrors.email = 'Email is invalid';
    }
    
    if (!paymentData.amount || parseFloat(paymentData.amount) <= 0) {
      newErrors.amount = 'Amount must be greater than 0';
    } else if (parseFloat(paymentData.amount) > 200000) {
      newErrors.amount = 'Amount cannot exceed ₹2,00,000';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Form handlers
  const handleChange = (e) => {
    const { name, value } = e.target;
    setPaymentData(prev => ({
      ...prev,
      [name]: value
    }));
    
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
    if (apiError) {
      setApiError("");
    }
  };

  const handlePhoneChange = (e) => {
     let value = e.target.value.replace(/\D/g, ""); 

    if (value.length === 1 && /^[1-5]$/.test(value)) {
      return;
    }

    if (value.length > 10) return;

    setPaymentData(prev => ({
      ...prev,
      phone: value
    }));
  };

  const handleAmountChange = (e) => {
    const value = e.target.value.replace(/[^\d.]/g, '');
    const decimalCount = (value.match(/\./g) || []).length;
    
    if (decimalCount <= 1) {
      setPaymentData(prev => ({
        ...prev,
        amount: value
      }));
    }
  };

  const formatAmountDisplay = (amount) => {
    if (!amount) return '0';
    const num = parseFloat(amount);
    return num.toLocaleString('en-IN', {
      maximumFractionDigits: 2,
      minimumFractionDigits: 0
    });
  };

  // Helper function to parse response safely
  const parseResponseSafely = async (response) => {
    const responseText = await response.text();
    
    if (responseText.trim().startsWith('<!DOCTYPE') || responseText.trim().startsWith('<')) {
      throw new Error(`Server returned HTML error page. Status: ${response.status}`);
    }
    
    try {
      return JSON.parse(responseText);
    } catch (parseError) {
      throw new Error(`Invalid JSON response. Status: ${response.status}`);
    }
  };

  // Main payment handler - UPDATED TO HANDLE CLOSING
  const handlePay = async () => {
    if (!validateForm()) return;
    
    setIsSubmitting(true);
    setApiError("");
    
    try {
      // Step 1: Create order on backend
      const createOrderUrl = getApiUrl('/create-payment-order');
      const response = await fetch(createOrderUrl, {
        method: 'POST',
        headers: getApiHeaders(),
        body: JSON.stringify({
          ...paymentData,
          bank_name: bankDetails[selectedBank]?.name || selectedBank
        })
      });

      const data = await parseResponseSafely(response);
      
      if (!data.success) {
        throw new Error(data.message || 'Failed to create order');
      }
      
      // Check if Razorpay is loaded
      if (!window.Razorpay) {
        throw new Error('Payment gateway not loaded. Please refresh the page.');
      }
      
      // Step 2: Initialize Razorpay checkout
      const options = {
        key: data.key,
        amount: data.amount * 100,
        currency: data.currency,
        name: 'ASIATIC STOCK & SECURITIES LTD',
        description: `Payment to ${bankDetails[selectedBank]?.name || selectedBank}`,
        order_id: data.order_id,
        handler: async function(response) {
          // Show processing alert
          showPaymentAlert('processing', 'Verifying Payment', 'Please wait while we verify your payment...');
          
          try {
            // Step 3: Verify payment on backend
            const verifyUrl = getApiUrl('/verify-payment');
            const verifyResponse = await fetch(verifyUrl, {
              method: 'POST',
              headers: getApiHeaders(),
              body: JSON.stringify({
                razorpay_order_id: response.razorpay_order_id,
                razorpay_payment_id: response.razorpay_payment_id,
                razorpay_signature: response.razorpay_signature,
                bank_name: bankDetails[selectedBank]?.name || selectedBank,
                ucc_code: paymentData.ucc
              })
            });

            const verifyData = await parseResponseSafely(verifyResponse);
            
            if (verifyData.success) {
              // Close processing alert and show success
              Swal.close();
              showPaymentSuccess({
                razorpay_payment_id: response.razorpay_payment_id,
                amount: paymentData.amount,
                bank: bankDetails[selectedBank]?.name,
                order_id: response.razorpay_order_id,
                ucc: paymentData.ucc
              });
            } else {
              Swal.close();
              showPaymentError(
                'Payment Verification Failed',
                'We were unable to verify your payment. Please contact support with your Payment ID.',
                response.razorpay_payment_id
              );
            }
          } catch (verifyError) {
            Swal.close();
            showPaymentError(
              'Verification Error',
              'An error occurred while verifying your payment. Please contact support.',
              response.razorpay_payment_id
            );
          }
        },
        prefill: {
          name: data.name,
          email: data.email,
          contact: data.phone
        },
        notes: {
          ucc: data.ucc,
          bank: bankDetails[selectedBank]?.name || selectedBank
        },
        theme: {
          color: '#007bff'
        },
        modal: {
          ondismiss: function() {
            setIsSubmitting(false);
            // Show cancelled alert
            Swal.fire({
              title: 'Payment Cancelled',
              text: 'You cancelled the payment process.',
              icon: 'warning',
              width: '450px',
              confirmButtonText: 'OK',
              confirmButtonColor: '#ffc107',
            }).then(() => {
              closePayment(true); // Force close
            });
          }
        }
      };
      
      const rzp = new window.Razorpay(options);
      rzp.open();
      
      rzp.on('payment.failed', function(response) {
        setIsSubmitting(false);
        
        let errorTitle = 'Payment Failed';
        let errorMessage = 'Payment failed. Please try again.';
        const paymentId = response.error.metadata?.payment_id || '';
        
        if (response.error && response.error.description) {
          const desc = response.error.description;
          
          if (desc.includes('International cards') || desc.includes('international')) {
            errorTitle = 'International Card Not Supported';
            errorMessage = 'International cards are not supported. Please use a domestic card or other payment methods.';
          } else if (desc.includes('insufficient') || desc.includes('funds')) {
            errorTitle = 'Insufficient Funds';
            errorMessage = 'Insufficient funds in your account. Please check your balance and try again.';
          } else if (desc.includes('declined') || desc.includes('rejected')) {
            errorTitle = 'Transaction Declined';
            errorMessage = 'Your bank declined the transaction. Please contact your bank or try a different payment method.';
          } else if (desc.includes('timeout') || desc.includes('expired')) {
            errorTitle = 'Session Expired';
            errorMessage = 'Payment session expired. Please try again.';
          } else {
            errorMessage = desc;
          }
        }
        
        showPaymentError(errorTitle, errorMessage, paymentId);
      });
      
    } catch (error) {
      setApiError(error.message);
      setIsSubmitting(false);
      showPaymentError('Payment Error', error.message || 'An error occurred while processing payment.');
    }
  };

  // Close on Escape key - UPDATED
  useEffect(() => {
    const handleEsc = (e) => {
      if (e.key === 'Escape') {
        if (showPayment) {
          if (!isSubmitting) {
            closePayment();
          } else {
            // If submitting, show warning
            Swal.fire({
              title: 'Payment in Progress',
              text: 'Payment is being processed. Please wait...',
              icon: 'warning',
              confirmButtonText: 'OK',
              confirmButtonColor: '#ffc107'
            });
          }
        }
        if (showPopup) closePopup();
      }
    };
    
    window.addEventListener('keydown', handleEsc);
    return () => window.removeEventListener('keydown', handleEsc);
  }, [showPayment, showPopup, isSubmitting]);

  // Add CSS styles for SweetAlert
  useEffect(() => {
    const style = document.createElement('style');
    style.textContent = `
      .payment-status-popup {
        border-radius: 12px;
        padding: 20px;
      }
      
      .payment-success-container, .payment-error-container {
        font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
      }
      
      .payment-details-card {
        background: #f8f9fa;
        border-radius: 10px;
        padding: 20px;
        margin: 20px 0;
      }
      
      .detail-row {
        display: flex;
        justify-content: space-between;
        align-items: center;
        padding: 8px 0;
        border-bottom: 1px solid #e9ecef;
      }
      
      .detail-row:last-child {
        border-bottom: none;
      }
      
      .detail-label {
        color: #6c757d;
        font-size: 0.9em;
        font-weight: 500;
      }
      
      .detail-value {
        color: #212529;
        font-weight: 500;
        text-align: right;
      }
      
      .payment-status-button {
        border-radius: 6px;
        padding: 10px 30px;
        font-weight: 600;
      }
      
      .success-header, .error-header {
        text-align: center;
        margin-bottom: 20px;
      }
      
      .success-icon, .error-icon {
        display: flex;
        justify-content: center;
        margin-bottom: 10px;
      }
      
      /* Make sure SweetAlert is on top */
      .swal2-container {
        z-index: 10001 !important;
      }
    `;
    document.head.appendChild(style);
    
    return () => {
      document.head.removeChild(style);
    };
  }, []);

  return (
    <section className="about about--style1 bg-color xts-section">
      <div className="container">
        <div>
          <h4>list of client bank accounts</h4>
          <p>Pursuant to SEBI Circular No. SEBI/HO/MIRSD-PoD-1/P/CIR/2023/84 dated 08-jun-2023</p>

          <div className="table-responsive">
            <table className="table table-bordered">
              <thead>
                <tr>
                  <th>Company's Name</th>
                  <th>Bank Name</th>
                  <th>Account Number</th>
                  <th>IFSC CODE</th>
                  <th>Valid UPI ID</th>
                  <th>QR/Online</th>
                </tr>
              </thead>

              <tbody>
                {/* AXIS BANK Row */}
                <tr>
                  <td><b>ASIATIC STOCK &amp; SECURITIES LTD</b></td>
                  <td>AXIS BANK LTD</td>
                  <td>917020074476573</td>
                  <td>UTIB0000007</td>
                  <td>asiatic.rzp.brk@validaxis</td>
                  <td>
                    <button
                      className="btn btn-primary btn-sm"
                      style={{ width: "90px" }}
                      onClick={() => openPayment("AXIS BANK LTD")}
                    >
                      <i className="bi bi-credit-card me-1"></i> Pay Online
                    </button>
                  </td>
                </tr>

                {/* ICICI BANK Row */}
                <tr>
                  <td><b>ASIATIC STOCK &amp; SECURITIES LTD</b></td>
                  <td>ICICI BANK LTD</td>
                  <td>000705036698</td>
                  <td>ICIC0000007</td>
                  <td>asiatic.brk@validicici</td>
                  <td>
                    <button
                      className="btn btn-primary btn-sm"
                      style={{ width: "90px" }}
                      onClick={() => openPopup("/assets/images/ICICI upi-qr-code.png")}
                    >
                      View QR
                    </button>
                  </td>
                </tr>

                {/* AU SMALL BANK Row */}
                <tr>
                  <td><b>ASIATIC STOCK &amp; SECURITIES LTD</b></td>
                  <td>AU SMALL BANK</td>
                  <td>2302210351450794</td>
                  <td>AUBL0002103</td>
                  <td>asiatic.brk@validau</td>
                  <td>  <button
                      className="btn btn-primary btn-sm"
                      style={{ width: "90px" }}
                      onClick={() => openPopup("/assets/images/au_upi_handle.jpeg")}
                    >
                      View QR
                    </button></td>
                </tr>

              </tbody>
            </table>
          </div>

          <p>Investors are requested to note that Asiatic Stock & Securities ltd is permitted to receive/pay money from/to investors through designated bank accounts only named as Client Bank Accounts (USCNBA). Asiatic Stock & Securities ltd is also required to disclose these client bank accounts (USCNBA) to stock Exchange. Hence, you are requested to use the following Client Bank Accounts (USCNBA) Only for the purpose of dealings in your trading account with us. The details of these client bank accounts are also displayed by Stock Exchanges on their website under "Member Directory"</p>

        </div>

        {/* QR Code Popup Modal */}
        {showPopup && (
          <div
            className="popup-overlay"
            onClick={closePopup}
            style={{
              position: "fixed",
              top: 0,
              left: 0,
              width: "100%",
              height: "100%",
              background: "rgba(0,0,0,0.6)",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              zIndex: 9999
            }}
          >
            <div
              className="popup-content"
              onClick={(e) => e.stopPropagation()}
              style={{
                background: "#fff",
                padding: "20px",
                borderRadius: "6px",
                maxWidth: "90%",
                maxHeight: "90%",
                position: "relative",
              }}
            >
              <img
                src={popupImage}
                alt="QR Code"
                style={{ maxWidth: "100%", maxHeight: "80vh" }}
              />
              <button
                className="btn btn-danger"
                onClick={closePopup}
                style={{ 
                  position: "absolute",
                  borderRadius: "30px 0 30px 30px",
                  top: 0,
                  right: 0,
                  zIndex: 1,
                  padding: "5px 10px"
                }}
              >
                X
              </button>
            </div>
          </div>
        )}

        {/* Razorpay Payment Popup - WILL NOW CLOSE ON SUCCESS */}
        {showPayment && (
          <div 
            className="popup-overlay"
            style={{ 
              position: "fixed", 
              inset: 0, 
              background: "rgba(0,0,0,0.6)", 
              display: "flex", 
              justifyContent: "center", 
              alignItems: "center", 
              zIndex: 10000,
              padding: "20px",
              overflowY: "scroll"
            }}
            onClick={(e) => {
              if (e.target.className === "popup-overlay" && !isSubmitting) {
                closePayment();
              }
            }}
          >
            <div style={{ 
              background: "#fff", 
              padding: "30px", 
              width: "100%",
              maxWidth: "450px",
              borderRadius: "10px",
              boxShadow: "0 10px 30px rgba(0,0,0,0.2)",
              position: "relative"
            }}>
              <div className="d-flex justify-content-between align-items-center mb-3">
                <h6 className="mb-0">
                  <i className="bi bi-bank me-2"></i>
                  Pay to ASIATIC STOCK &amp; SECURITIES LTD
                </h6>
                <button
                  className="btn btn-danger"
                  onClick={() => closePayment(true)} // Force close
                  disabled={isSubmitting}
                  style={{ 
                    position: "absolute",
                    borderRadius: "30px 0 30px 30px",
                    top: 0,
                    right: 0,
                    zIndex: 1,
                    padding: "5px 10px"
                  }}
                >
                  X
                </button>
              </div>

              {/* API Error Display */}
              {apiError && (
                <div className="alert alert-danger alert-dismissible fade show" role="alert">
                  <i className="bi bi-exclamation-triangle me-2"></i>
                  <strong>API Error:</strong> {apiError}
                  <button 
                    type="button" 
                    className="btn-close" 
                    onClick={() => setApiError("")}
                  ></button>
                </div>
              )}

              <div className="mb-1">
                <label className="form-label" style={{ fontSize: "14px", marginBottom: "4px" }}>Full Name *</label>
                <input
                  type="text"
                  name="name"
                  placeholder="Enter your full name"
                  className={`form-control ${errors.name ? 'is-invalid' : ''}`}
                  value={paymentData.name}
                  onChange={handleChange}
                  disabled={isSubmitting}
                />
                {errors.name && <div className="invalid-feedback">{errors.name}</div>}
              </div>

              <div className="mb-1">
                <label className="form-label" style={{ fontSize: "14px", marginBottom: "4px" }}>UCC Code *</label>
                <input
                  type="text"
                  name="ucc"
                  placeholder="Enter UCC code"
                  className={`form-control ${errors.ucc ? 'is-invalid' : ''}`}
                  value={paymentData.ucc}
                  onChange={handleChange}
                  disabled={isSubmitting}
                />
                {errors.ucc && <div className="invalid-feedback">{errors.ucc}</div>}
              </div>

              <div className="mb-1">
                <label className="form-label" style={{ fontSize: "14px", marginBottom: "4px" }}>Phone Number *</label>
                <input
                  type="tel"
                  name="phone"
                  maxLength={10}
                  placeholder="10-digit mobile number"
                  className={`form-control ${errors.phone ? 'is-invalid' : ''}`}
                  value={paymentData.phone}
                  onChange={handlePhoneChange}
                  disabled={isSubmitting}
                />
                {errors.phone && <div className="invalid-feedback">{errors.phone}</div>}
              </div>

              <div className="mb-1">
                <label className="form-label" style={{ fontSize: "14px", marginBottom: "4px" }}>Email Address</label>
                <input
                  type="email"
                  name="email"
                  placeholder="Enter your email (optional)"
                  className={`form-control ${errors.email ? 'is-invalid' : ''}`}
                  value={paymentData.email}
                  onChange={handleChange}
                  disabled={isSubmitting}
                />
                {errors.email && <div className="invalid-feedback">{errors.email}</div>}
              </div>

              <div className="mb-1">
                <label className="form-label" style={{ fontSize: "14px", marginBottom: "4px" }}>Amount (₹) *</label>
                <div className="input-group">
                  <span className="input-group-text">₹</span>
                  <input
                    type="text"
                    name="amount"
                    placeholder="Enter amount"
                    className={`form-control ${errors.amount ? 'is-invalid' : ''}`}
                    value={paymentData.amount}
                    onChange={handleAmountChange}
                    disabled={isSubmitting}
                    maxLength={6}
                  />
                </div>
                {errors.amount && (
                  <div className="invalid-feedback d-block">
                    {errors.amount}
                  </div>
                )}
              </div>

              <button 
                className="btn btn-primary w-100 py-2 mt-2 fw-bold" 
                onClick={handlePay}
                disabled={isSubmitting}
              >
                {isSubmitting ? (
                  <>
                    <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                    Processing...
                  </>
                ) : (
                  <>
                    <i className="bi bi-lock-fill me-2"></i>
                    Proceed to Pay ₹{formatAmountDisplay(paymentData.amount) || '0'}
                  </>
                )}
              </button>
              
            </div>
          </div>
        )}

      </div>
    </section>
  );
};

export default AccountDetailSection;